import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

let configured = false;

export interface AmplifyConfig {
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  readonly region: string;
}

export function configureAmplify(config: AmplifyConfig): void {
  if (configured) return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: { email: true },
      },
    },
  });
  configured = true;
}

export function configureAmplifyFromEnv(): void {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  // Accept either name — CDK output is "UserPoolClientId" so both spellings are in circulation.
  const userPoolClientId =
    import.meta.env.VITE_COGNITO_CLIENT_ID ?? import.meta.env.VITE_COGNITO_APP_CLIENT_ID;
  const region = import.meta.env.VITE_AWS_REGION ?? 'us-east-1';

  if (!userPoolId || !userPoolClientId) {
    // eslint-disable-next-line no-console
    console.warn(
      '[amplify] VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID not set — auth will fail',
    );
    return;
  }
  // Intentional: log enough to verify the correct pool is baked into the bundle without
  // leaking the full ID. Useful for diagnosing "Auth UserPool not configured" on CF Pages.
  // eslint-disable-next-line no-console
  console.info(
    `[amplify] configuring pool ${userPoolId.slice(0, userPoolId.indexOf('_') + 4)}… (${region})`,
  );
  configureAmplify({ userPoolId, userPoolClientId, region });
}

/** Returns the Cognito access token for the current session, or null. */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/** Returns the Cognito ID token string for the current session, or null. */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/** Returns the parsed access-token payload, or null. */
export async function getAccessTokenPayload(): Promise<Record<string, unknown> | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.accessToken?.payload;
    return payload ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Returns the parsed ID-token payload, or null. Custom attributes (e.g. custom:shop_id) live here, not in the access token. */
export async function getIdTokenPayload(): Promise<Record<string, unknown> | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    return payload ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
